/**
 * WorkflowAssignmentModal
 * Unified modal for team member assignment
 * - Used from QuickActionButtons for workflow transitions (Assign to 2D/3D)
 * - Used from TeamCard for general team management
 * - Supports multi-select with ability to deselect (remove) assigned members
 */
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const ROLE_CONFIG = {
  site_draughter: {
    label: 'Site Draughter',
    labelPlural: 'Site Draughters',
    description: 'Assign site draughters for on-site measurements',
    actionLabel: 'Save Assignments',
    color: 'orange',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    )
  },
  draughter_2d: {
    label: '2D Draughter',
    labelPlural: '2D Draughters',
    description: 'Assign one or more 2D Draughters to the project',
    actionLabel: 'Assign & Start 2D Phase',
    color: 'blue',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )
  },
  designer_3d: {
    label: '3D Designer',
    labelPlural: '3D Designers',
    description: 'Assign one or more 3D Designers to the project',
    actionLabel: 'Assign & Start 3D Phase',
    color: 'purple',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    )
  }
};

function WorkflowAssignmentModal({
  isOpen,
  onClose,
  projectId,
  roleType,
  targetStatus,
  onSuccess,
  currentTeamMembers = [],
  transitionWorkflow = false
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [initialSelectedIds, setInitialSelectedIds] = useState([]);

  const roleConfig = ROLE_CONFIG[roleType] || {
    label: roleType,
    labelPlural: roleType + 's',
    description: 'Assign team members to the project',
    color: 'gray',
    actionLabel: 'Save Assignments',
    icon: null
  };

  useEffect(() => {
    if (isOpen && projectId && roleType) {
      // Get IDs of currently assigned members for this role
      const currentIds = currentTeamMembers
        .filter(m => m.roleType === roleType)
        .map(m => m.user?.id || m.userId);

      setInitialSelectedIds(currentIds);
      setSelectedUserIds(currentIds);
      setError(null);
      fetchAvailableUsers();
    }
  }, [isOpen, projectId, roleType, currentTeamMembers]);

  const fetchAvailableUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/design-projects/${projectId}/team/available/${roleType}`);
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching available users:', err);
      setError('Failed to load available users');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Calculate diff between initial and current selection
  const toAdd = selectedUserIds.filter(id => !initialSelectedIds.includes(id));
  const toRemove = initialSelectedIds.filter(id => !selectedUserIds.includes(id));
  const hasChanges = toAdd.length > 0 || toRemove.length > 0;

  const handleSaveAssignments = async () => {
    // For workflow transitions, require at least one member
    if (transitionWorkflow && selectedUserIds.length === 0) {
      setError('Please select at least one team member to assign');
      return;
    }

    // For team management without workflow, allow if there are changes
    if (!transitionWorkflow && !hasChanges) {
      onClose();
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Step 1: Remove deselected members
      if (toRemove.length > 0) {
        const removePromises = toRemove.map(userId => {
          const member = currentTeamMembers.find(
            m => (m.user?.id === userId || m.userId === userId) && m.roleType === roleType
          );
          if (member) {
            return api.delete(`/design-projects/${projectId}/team/${member.id}`);
          }
          return Promise.resolve();
        });
        await Promise.all(removePromises);
      }

      // Step 2: Add newly selected members
      if (toAdd.length > 0) {
        const addPromises = toAdd.map(userId =>
          api.post(`/design-projects/${projectId}/team`, {
            userId,
            roleType
          })
        );
        await Promise.all(addPromises);
      }

      // Step 3: Transition workflow if required and there are additions
      if (transitionWorkflow && targetStatus && toAdd.length > 0) {
        await api.patch(`/design-projects/${projectId}/workflow-status`, {
          newStatus: targetStatus
        });
      }

      // Show success toast
      const changes = [];
      if (toAdd.length > 0) changes.push(`${toAdd.length} added`);
      if (toRemove.length > 0) changes.push(`${toRemove.length} removed`);

      if (changes.length > 0) {
        toast.success(`${roleConfig.labelPlural}: ${changes.join(', ')}`);
      }

      onSuccess?.({
        userIds: selectedUserIds,
        addedIds: toAdd,
        removedIds: toRemove,
        roleType,
        newStatus: transitionWorkflow ? targetStatus : null
      });
      onClose();
    } catch (err) {
      console.error('Error saving team assignments:', err);
      setError(err.response?.data?.error || 'Failed to save assignments');
    } finally {
      setSubmitting(false);
    }
  };

  // Get button text based on context and changes
  const getButtonText = () => {
    if (submitting) return 'Saving...';

    if (transitionWorkflow) {
      if (selectedUserIds.length > 0) {
        return `Assign ${selectedUserIds.length} & Start Phase`;
      }
      return roleConfig.actionLabel;
    }

    // Non-workflow context
    if (!hasChanges) return 'No Changes';

    const parts = [];
    if (toAdd.length > 0) parts.push(`Add ${toAdd.length}`);
    if (toRemove.length > 0) parts.push(`Remove ${toRemove.length}`);
    return parts.join(', ');
  };

  if (!isOpen) return null;

  const colorClasses = {
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-800',
      button: 'bg-orange-600 hover:bg-orange-700',
      selected: 'border-orange-500 bg-orange-50 ring-2 ring-orange-500',
      icon: 'text-orange-600',
      checkbox: 'text-orange-600'
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-800',
      button: 'bg-blue-600 hover:bg-blue-700',
      selected: 'border-blue-500 bg-blue-50 ring-2 ring-blue-500',
      icon: 'text-blue-600',
      checkbox: 'text-blue-600'
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-800',
      button: 'bg-purple-600 hover:bg-purple-700',
      selected: 'border-purple-500 bg-purple-50 ring-2 ring-purple-500',
      icon: 'text-purple-600',
      checkbox: 'text-purple-600'
    },
    gray: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      text: 'text-gray-800',
      button: 'bg-gray-600 hover:bg-gray-700',
      selected: 'border-gray-500 bg-gray-50 ring-2 ring-gray-500',
      icon: 'text-gray-600',
      checkbox: 'text-gray-600'
    }
  };

  const colors = colorClasses[roleConfig.color] || colorClasses.gray;

  // Show all users (both available and assigned)
  const allUsers = users;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className={`p-4 border-b ${colors.border} ${colors.bg}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {roleConfig.icon && (
                <div className={colors.icon}>
                  {roleConfig.icon}
                </div>
              )}
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {transitionWorkflow ? `Assign ${roleConfig.labelPlural}` : `Manage ${roleConfig.labelPlural}`}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">{roleConfig.description}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/50 rounded-full transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Info Banner - different based on context */}
        {transitionWorkflow ? (
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-amber-800">
                Select team members. The project workflow will automatically transition after assignment.
              </p>
            </div>
          </div>
        ) : (
          <div className="px-4 py-3 bg-blue-50 border-b border-blue-200">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-blue-800">
                Check members to add, uncheck to remove. Changes are saved when you click the button below.
              </p>
            </div>
          </div>
        )}

        {/* Selection Summary */}
        {(selectedUserIds.length > 0 || hasChanges) && (
          <div className={`px-4 py-2 ${colors.bg} border-b ${colors.border}`}>
            <div className="flex items-center justify-between">
              <span className={`text-sm font-medium ${colors.text}`}>
                {selectedUserIds.length} {selectedUserIds.length === 1 ? 'member' : 'members'} selected
                {hasChanges && (
                  <span className="ml-2 text-gray-500 font-normal">
                    ({toAdd.length > 0 && `+${toAdd.length}`}{toAdd.length > 0 && toRemove.length > 0 && ', '}{toRemove.length > 0 && `-${toRemove.length}`})
                  </span>
                )}
              </span>
              {selectedUserIds.length > 0 && (
                <button
                  onClick={() => setSelectedUserIds([])}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                <p className="text-sm text-gray-500">Loading team members...</p>
              </div>
            </div>
          ) : allUsers.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-gray-600 font-medium">No available {roleConfig.labelPlural.toLowerCase()}</p>
              <p className="text-sm text-gray-500 mt-1">
                Make sure team members are assigned to the project's SID team
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                Team Members ({allUsers.length} available)
              </p>
              {allUsers.map(user => {
                const isSelected = selectedUserIds.includes(user.id);
                const wasInitiallyAssigned = initialSelectedIds.includes(user.id);
                return (
                  <button
                    key={user.id}
                    onClick={() => toggleUserSelection(user.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                      isSelected
                        ? colors.selected
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Checkbox */}
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isSelected
                          ? `${colors.checkbox} border-current bg-current`
                          : 'border-gray-300'
                      }`}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colors.bg} ${colors.text}`}>
                        <span className="text-sm font-medium">
                          {(user.nickname || user.fullName || '?').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-900">
                          {user.nickname || user.fullName}
                          {user.role === 'sid' && <span className="ml-1 text-xs text-purple-600">(SID)</span>}
                          {user.role === 'asid' && <span className="ml-1 text-xs text-blue-600">(ASID)</span>}
                        </p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Currently assigned badge */}
                      {wasInitiallyAssigned && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                          Assigned
                        </span>
                      )}
                      {/* Workload indicator */}
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        user.activeProjects === 0
                          ? 'bg-green-100 text-green-700'
                          : user.activeProjects <= 2
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                      }`}>
                        {user.activeProjects} active
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAssignments}
              disabled={submitting || (transitionWorkflow && selectedUserIds.length === 0) || (!transitionWorkflow && !hasChanges) || allUsers.length === 0}
              className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${colors.button}`}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                getButtonText()
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkflowAssignmentModal;
